import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Building2, Plus, Trash2, Search, MapPin, RefreshCw, X, Clock, PenLine, Link } from 'lucide-react';
import { dashboardApi } from '../../services/tenantApi';

const StatusBadge = ({ status }) => {
  if (status === 'pending_scraping') {
    return (
      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs flex items-center gap-1">
        <Clock className="h-3 w-3" /> Pending scraping
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-xs">
      Active
    </Badge>
  );
};

const CompetitorHotelManagement = () => {
  const [configured, setConfigured] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [loadingConfigured, setLoadingConfigured] = useState(true);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Manual entry state
  const [manualName, setManualName] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  const [addingManual, setAddingManual] = useState(false);

  useEffect(() => {
    fetchConfigured();
    handleSearch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConfigured = async () => {
    setLoadingConfigured(true);
    try {
      const data = await dashboardApi.getMyCompetitorHotels();
      setConfigured(data.competitor_hotels || []);
      if (data.notice) setNotice(data.notice);
      else setNotice(null);
    } catch (err) {
      setError('Failed to load configured competitors.');
    } finally {
      setLoadingConfigured(false);
    }
  };

  const handleSearch = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const data = await dashboardApi.searchAvailableCompetitorHotels({
        search: searchQuery.trim() || undefined,
        location: locationFilter.trim() || undefined,
        limit: 30,
      });
      setSearchResults(data.hotels || []);
      setSearchPerformed(true);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [searchQuery, locationFilter]);

  const handleAdd = async (hotel) => {
    setAddingId(hotel.id);
    setError(null);
    try {
      await dashboardApi.addCompetitorHotel({
        ota_hotel_name: hotel.name,
        ota_hotel_id: hotel.id,
        ota: 'booking.com',
      });
      setSuccessMsg(`"${hotel.name}" added as a competitor.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      setSearchResults((prev) => prev.filter((h) => h.id !== hotel.id));
      await fetchConfigured();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to add hotel.';
      setError(msg);
    } finally {
      setAddingId(null);
    }
  };

  const handleRemove = async (competitor) => {
    setRemovingId(competitor.id);
    setError(null);
    try {
      await dashboardApi.removeCompetitorHotel(competitor.id);
      setSuccessMsg(`"${competitor.ota_hotel_name}" removed.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      setConfigured((prev) => prev.filter((c) => c.id !== competitor.id));
    } catch (err) {
      setError('Failed to remove competitor hotel.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setLocationFilter('');
    setSearchResults([]);
    setSearchPerformed(false);
  };

  const handleAddManual = async () => {
    if (!manualName.trim()) return;
    setAddingManual(true);
    setError(null);
    try {
      await dashboardApi.addCompetitorHotel({
        ota_hotel_name: manualName.trim(),
        ota_hotel_id: manualName.trim(),
        ota: 'booking.com',
        hotel_url: manualUrl.trim() || undefined,
        scraping_status: 'pending_scraping',
      });
      setSuccessMsg(`"${manualName.trim()}" added — pending scraping setup by admin.`);
      setTimeout(() => setSuccessMsg(null), 4000);
      setManualName('');
      setManualUrl('');
      await fetchConfigured();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to add hotel.';
      setError(msg);
    } finally {
      setAddingManual(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {successMsg && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          <span className="text-sm">{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)}><X className="h-4 w-4" /></button>
        </div>
      )}
      {notice && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
          <span className="text-sm">⚠️ {notice} — go to <strong>Hotels</strong> tab to add your property first.</span>
        </div>
      )}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Configured competitors */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Configured Competitors</span>
              </CardTitle>
              <CardDescription>
                Hotels tracked in your rate comparison and pricing reports
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchConfigured} disabled={loadingConfigured}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingConfigured ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingConfigured ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          ) : configured.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">
              No competitor hotels configured yet. Use the search below to add some.
            </div>
          ) : (
            <div className="space-y-2">
              {configured.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between px-4 py-3 border rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.ota_hotel_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground capitalize">{c.ota}</span>
                        <StatusBadge status={c.scraping_status} />
                        {c.hotel_url && (
                          <a href={c.hotel_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                            <Link className="h-3 w-3" /> OTA page
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(c)}
                    disabled={removingId === c.id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    {removingId === c.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                {configured.length} competitor{configured.length !== 1 ? 's' : ''} configured
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual entry */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PenLine className="h-5 w-5" />
            <span>Add Hotel Manually</span>
          </CardTitle>
          <CardDescription>
            Hotel not in the list above? Enter it manually — admin will configure PromptCloud to start scraping it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Hotel name (as it appears on Booking.com)…"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddManual()}
              />
            </div>
            <div className="flex-1 relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Booking.com URL (optional but recommended)…"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddManual()}
              />
            </div>
            <Button onClick={handleAddManual} disabled={addingManual || !manualName.trim()}>
              {addingManual ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Request
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            This competitor will be saved with <strong>Pending scraping</strong> status.
            Pricing data will appear once admin configures the data feed (usually within 24–48 hours).
          </p>
        </CardContent>
      </Card>

      {/* Search & add from price feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>Add Competitor Hotel</span>
          </CardTitle>
          <CardDescription>
            Search hotels available in the price feed and add them to your competitor set
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Hotel name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Location (city)…"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Search
              </Button>
              {searchPerformed && (
                <Button variant="outline" onClick={clearSearch}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Results */}
          {searchPerformed && (
            <div>
              {searchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
                  No available hotels found. Try a different search term or location.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</p>
                  {searchResults.map((hotel) => (
                    <div
                      key={hotel.id}
                      className="flex items-center justify-between px-4 py-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{hotel.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center space-x-1">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{hotel.location}</span>
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAdd(hotel)}
                        disabled={addingId === hotel.id}
                        className="ml-4 shrink-0"
                      >
                        {addingId === hotel.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <><Plus className="h-4 w-4 mr-1" />Add</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompetitorHotelManagement;
